import { useCallback, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import type { IntegrationsSearch, IntegrationsTab } from "..";
import { useOverview, type SignalOut } from "../api";
import { AdminTab } from "../components/AdminTab";
import { EntityQuickDrawer } from "../components/EntityQuickDrawer";
import { HowToTab } from "../components/HowToTab";
import { IncidentDrawer } from "../components/IncidentDrawer";
import { IntegrationsLandscapeTab } from "../components/IntegrationsTab";
import { LevelTab } from "../components/LevelTab";
import { NewChangedTab } from "../components/NewChangedTab";
import { OverviewTab } from "../components/OverviewTab";
import { WatchlistTab } from "../components/WatchlistTab";

/** Monitors › Integrations (COPS-3). Five tabs; the daily read is Overview +
 *  Alerts, everything else is depth. Tab + drawer state live in the URL so
 *  the morning link "…?tab=alerts&incident=…" opens straight onto the thing. */
export function IntegrationsPage() {
  const search = useSearch({ from: "/integrations" }) as IntegrationsSearch;
  const navigate = useNavigate();
  const canAdmin = useCan("integrations.admin");
  const requested = search.tab ?? "overview";
  // Same idiom as code_rag's manage tab: a direct URL hit without the
  // permission falls back to overview rather than 403-ing.
  const tab: IntegrationsTab = requested === "admin" && !canAdmin ? "overview" : requested;

  const go = useCallback(
    (next: Partial<IntegrationsSearch>) => {
      void navigate({
        to: "/integrations",
        search: (prev: IntegrationsSearch) => ({ ...prev, ...next }),
      });
    },
    [navigate],
  );

  const [presetFlag, setPresetFlag] = useState(false);
  const overview = useOverview();

  const openIncident = useCallback(
    (id: string, flag = false) => {
      setPresetFlag(flag);
      go({ incident: id, entity: undefined, entityLevel: undefined });
    },
    [go],
  );
  const openEntity = useCallback(
    (level: string, keyPath: string) => {
      go({ entity: keyPath, entityLevel: level, incident: undefined });
    },
    [go],
  );
  const openSignal = useCallback(
    (s: SignalOut) => {
      if (s.incident_id) openIncident(s.incident_id);
      else openEntity(s.level, s.key_path);
    },
    [openIncident, openEntity],
  );
  const closeDrawers = useCallback(() => {
    go({ incident: undefined, entity: undefined, entityLevel: undefined, expanded: undefined });
  }, [go]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground">
          Event-clock anomaly detection over every inbound feed — few incidents, early
          signals, and drill into any integration, company, or location.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => go({ tab: v as IntegrationsTab })}>
        <TabsList variant="underline">
          <TabsTrigger
            value="overview"
            count={
              overview.data
                ? overview.data.l1_open + overview.data.l2_open + overview.data.l3_open ||
                  undefined
                : undefined
            }
          >
            Overview
          </TabsTrigger>
          <TabsTrigger value="integrations" count={overview.data?.l1_open || undefined}>
            Integrations
          </TabsTrigger>
          <TabsTrigger value="companies" count={overview.data?.l2_open || undefined}>
            Companies
          </TabsTrigger>
          <TabsTrigger value="locations" count={overview.data?.l3_open || undefined}>
            Locations
          </TabsTrigger>
          <TabsTrigger value="new" count={overview.data?.new_this_week || undefined}>
            New &amp; Changed
          </TabsTrigger>
          <TabsTrigger value="watchlist">Watchlist</TabsTrigger>
          {canAdmin ? <TabsTrigger value="admin">Admin</TabsTrigger> : null}
          <TabsTrigger value="how">How to Use</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          {overview.data ? (
            <OverviewTab
              data={overview.data}
              onOpenIncident={openIncident}
              onOpenSignal={openSignal}
              onGoTab={(t) => go({ tab: t as IntegrationsTab })}
            />
          ) : overview.error ? (
            <p className="text-sm text-destructive">{String(overview.error)}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </TabsContent>
        <TabsContent value="integrations" className="pt-4">
          {overview.data ? (
            <IntegrationsLandscapeTab
              data={overview.data}
              onOpenIncident={openIncident}
              onOpenEntity={openEntity}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </TabsContent>
        <TabsContent value="companies" className="pt-4">
          <LevelTab level="L2" onOpenIncident={openIncident} onOpenEntity={openEntity} />
        </TabsContent>
        <TabsContent value="locations" className="pt-4">
          <LevelTab level="L3" onOpenIncident={openIncident} onOpenEntity={openEntity} />
        </TabsContent>
        <TabsContent value="new" className="pt-4">
          <NewChangedTab onOpenEntity={openEntity} />
        </TabsContent>
        <TabsContent value="watchlist" className="pt-4">
          <WatchlistTab onOpenEntity={openEntity} />
        </TabsContent>
        {canAdmin ? (
          <TabsContent value="admin" className="pt-4">
            <AdminTab />
          </TabsContent>
        ) : null}
        <TabsContent value="how" className="pt-4">
          <HowToTab />
        </TabsContent>
      </Tabs>

      <IncidentDrawer
        incidentId={search.incident ?? null}
        presetFlag={presetFlag}
        expanded={!!search.expanded && !!search.incident}
        onExpandedChange={(v) => go({ expanded: v ? true : undefined })}
        onClose={closeDrawers}
        onOpenEntity={openEntity}
      />
      <EntityQuickDrawer
        level={search.entity ? (search.entityLevel ?? "L1") : null}
        keyPath={search.entity ?? null}
        expanded={!!search.expanded && !!search.entity}
        onExpandedChange={(v) => go({ expanded: v ? true : undefined })}
        onClose={closeDrawers}
        onOpenEntity={openEntity}
      />
    </div>
  );
}
